/**
 * Alshallal Pastry Company HR System - HR Document Verification Page
 * Phase 3: Document Management
 * HR view for reviewing, approving, and rejecting employee documents
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  AlertCircle,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Calendar,
  User,
  Download,
  Loader2,
  Scan
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { isExpired, isExpiringSoon } from '@/lib/expiry';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import OCRStatusBadge from '@/components/documents/OCRStatusBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_STYLES = {
  pending: { bg: 'bg-amber-100 text-amber-800', icon: Clock },
  approved: { bg: 'bg-green-100 text-green-800', icon: CheckCircle },
  rejected: { bg: 'bg-red-100 text-red-800', icon: XCircle },
};

const STATUS_LABELS = {
  pending: { en: 'Pending', ar: 'قيد المراجعة' },
  approved: { en: 'Approved', ar: 'موافق عليه' },
  rejected: { en: 'Rejected', ar: 'مرفوض' },
};

const HRDocumentVerificationPage = () => {
  const { token, hasRole } = useAuth();
  const { t, language, isRTL } = useLanguage();
  const navigate = useNavigate();

  // State
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [filters, setFilters] = useState({
    status: 'pending', // Default to pending
    employee_id: '',
    document_type_id: '',
    expiring_within_days: '',
    search: ''
  });

  // Review dialog
  const [reviewDialog, setReviewDialog] = useState({ open: false, document: null, action: null });
  const [reviewReason, setReviewReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // View dialog
  const [viewDialog, setViewDialog] = useState({ open: false, document: null });

  // RBAC check
  useEffect(() => {
    if (!hasRole('hr_assistant', 'hr_manager', 'hr_admin', 'super_admin')) {
      navigate('/documents');
    }
  }, [hasRole, navigate]);

  // Fetch document types
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const response = await axios.get(`${API_URL}/documents/types`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDocumentTypes(response.data.document_types || []);
      } catch (err) {
        console.error('Failed to fetch document types:', err);
      }
    };
    fetchTypes();
  }, [token]);

  // Fetch employees for filter
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axios.get(`${API_URL}/employees/?page_size=100`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEmployees(response.data.employees || []);
      } catch (err) {
        console.error('Failed to fetch employees:', err);
      }
    };
    fetchEmployees();
  }, [token]);

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());
      
      if (filters.status && filters.status !== 'all') {
        params.append('status', filters.status);
      }
      if (filters.employee_id) {
        params.append('employee_id', filters.employee_id);
      }
      if (filters.document_type_id) {
        params.append('document_type_id', filters.document_type_id);
      }
      if (filters.expiring_within_days) {
        params.append('expiring_within_days', filters.expiring_within_days);
      }

      const response = await axios.get(`${API_URL}/documents/?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      let docs = response.data.documents || [];
      
      // Client-side search filter (for employee name)
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        docs = docs.filter(doc => 
          doc.employee_name?.toLowerCase().includes(searchLower) ||
          doc.document_type_name?.toLowerCase().includes(searchLower) ||
          doc.file_name?.toLowerCase().includes(searchLower)
        );
      }

      setDocuments(docs);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      setError(err.response?.data?.detail?.error || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [token, page, pageSize, filters]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleReviewSubmit = async () => {
    if (!reviewDialog.document || reviewReason.length < 10) return;

    try {
      setSubmitting(true);
      await axios.post(
        `${API_URL}/documents/${reviewDialog.document.id}/review`,
        {
          status: reviewDialog.action,
          reason: reviewReason
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setReviewDialog({ open: false, document: null, action: null });
      setReviewReason('');
      fetchDocuments();
    } catch (err) {
      console.error('Review failed:', err);
      setError(err.response?.data?.detail?.error || 'Review failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (document) => {
    try {
      const response = await axios.get(
        `${API_URL}/documents/${document.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', document.file_name);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      setError('Download failed');
    }
  };

  const clearFilters = () => {
    setFilters({
      status: 'pending',
      employee_id: '',
      document_type_id: '',
      expiring_within_days: '',
      search: ''
    });
    setPage(1);
  };

  const getStatusLabel = (status) => {
    const labels = STATUS_LABELS[status];
    return labels ? (language === 'ar' ? labels.ar : labels.en) : status;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'PP');
    } catch {
      return dateStr;
    }
  };

  // Expiry helpers — single source of truth in '@/lib/expiry'
  // (handles backend-computed `is_expired` / `days_until_expiry` fields and
  // fixes the timezone bug where new Date("YYYY-MM-DD") would parse as UTC).

  // Stats
  const pendingCount = documents.filter(d => d.status === 'pending').length;
  const hasActiveFilters = filters.status !== 'pending' || filters.employee_id || filters.document_type_id || filters.expiring_within_days || filters.search;

  return (
    <div className="space-y-6" data-testid="hr-document-verification-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isRTL ? 'التحقق من المستندات' : 'Document Verification'}
          </h1>
          <p className="text-slate-600 mt-1">
            {isRTL ? 'مراجعة واعتماد مستندات الموظفين' : 'Review and verify employee documents'}
          </p>
        </div>
        <Button variant="outline" onClick={fetchDocuments} disabled={loading}>
          <RefreshCw className={cn("h-4 w-4 me-2", loading && "animate-spin")} />
          {isRTL ? 'تحديث' : 'Refresh'}
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="ms-auto text-red-500 hover:text-red-700">
            ×
          </button>
        </div>
      )}

      {/* Filters Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            {isRTL ? 'الفلاتر' : 'Filters'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="space-y-2">
              <Label className="text-xs">{isRTL ? 'بحث' : 'Search'}</Label>
              <div className="relative">
                <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={isRTL ? 'اسم الموظف...' : 'Employee name...'}
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="ps-9"
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="space-y-2">
              <Label className="text-xs">{isRTL ? 'الحالة' : 'Status'}</Label>
              <Select
                value={filters.status}
                onValueChange={(v) => handleFilterChange('status', v)}
              >
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder={isRTL ? 'الكل' : 'All'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'الكل' : 'All'}</SelectItem>
                  <SelectItem value="pending">{isRTL ? 'قيد المراجعة' : 'Pending'}</SelectItem>
                  <SelectItem value="approved">{isRTL ? 'موافق عليه' : 'Approved'}</SelectItem>
                  <SelectItem value="rejected">{isRTL ? 'مرفوض' : 'Rejected'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Employee Filter */}
            <div className="space-y-2">
              <Label className="text-xs">{isRTL ? 'الموظف' : 'Employee'}</Label>
              <Select
                value={filters.employee_id}
                onValueChange={(v) => handleFilterChange('employee_id', v === 'all' ? '' : v)}
              >
                <SelectTrigger data-testid="employee-filter">
                  <SelectValue placeholder={isRTL ? 'الكل' : 'All Employees'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'جميع الموظفين' : 'All Employees'}</SelectItem>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.personal?.first_name} {emp.personal?.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Document Type Filter */}
            <div className="space-y-2">
              <Label className="text-xs">{isRTL ? 'نوع المستند' : 'Document Type'}</Label>
              <Select
                value={filters.document_type_id}
                onValueChange={(v) => handleFilterChange('document_type_id', v === 'all' ? '' : v)}
              >
                <SelectTrigger data-testid="type-filter">
                  <SelectValue placeholder={isRTL ? 'الكل' : 'All Types'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'جميع الأنواع' : 'All Types'}</SelectItem>
                  {documentTypes.map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      {language === 'ar' && type.name_ar ? type.name_ar : type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Expiry Filter */}
            <div className="space-y-2">
              <Label className="text-xs">{isRTL ? 'انتهاء الصلاحية' : 'Expiring Within'}</Label>
              <Select
                value={filters.expiring_within_days}
                onValueChange={(v) => handleFilterChange('expiring_within_days', v === 'all' ? '' : v)}
              >
                <SelectTrigger data-testid="expiry-filter">
                  <SelectValue placeholder={isRTL ? 'أي وقت' : 'Any time'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'أي وقت' : 'Any time'}</SelectItem>
                  <SelectItem value="7">{isRTL ? '7 أيام' : '7 days'}</SelectItem>
                  <SelectItem value="30">{isRTL ? '30 يوم' : '30 days'}</SelectItem>
                  <SelectItem value="60">{isRTL ? '60 يوم' : '60 days'}</SelectItem>
                  <SelectItem value="90">{isRTL ? '90 يوم' : '90 days'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mt-4 flex justify-end">
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                {isRTL ? 'مسح الفلاتر' : 'Clear Filters'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Documents Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : documents.length === 0 ? (
            <div className="py-12 text-center">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {isRTL ? 'لا توجد مستندات' : 'No Documents Found'}
              </h3>
              <p className="text-slate-600">
                {filters.status === 'pending'
                  ? (isRTL ? 'لا توجد مستندات تحتاج مراجعة' : 'No documents pending review')
                  : (isRTL ? 'لا توجد مستندات تطابق معايير البحث' : 'No documents match your filter criteria')}
              </p>
              {hasActiveFilters && (
                <Button variant="outline" className="mt-4" onClick={clearFilters}>
                  {isRTL ? 'عرض جميع المستندات' : 'Show All Documents'}
                </Button>
              )}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{isRTL ? 'الموظف' : 'Employee'}</TableHead>
                    <TableHead>{isRTL ? 'نوع المستند' : 'Document Type'}</TableHead>
                    <TableHead>{isRTL ? 'تاريخ الرفع' : 'Uploaded'}</TableHead>
                    <TableHead>{isRTL ? 'تاريخ الانتهاء' : 'Expiry Date'}</TableHead>
                    <TableHead>{isRTL ? 'الحالة' : 'Status'}</TableHead>
                    {hasRole('hr_manager', 'hr_admin', 'super_admin') && (
                      <TableHead>{isRTL ? 'OCR' : 'OCR'}</TableHead>
                    )}
                    <TableHead className="text-end">{isRTL ? 'الإجراءات' : 'Actions'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.map((doc) => {
                    const StatusIcon = STATUS_STYLES[doc.status]?.icon || Clock;
                    const expired = isExpired(doc);
                    const expiringSoon = isExpiringSoon(doc);

                    return (
                      <TableRow key={doc.id} data-testid={`doc-row-${doc.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-slate-400" />
                            <button
                              className="font-medium text-primary hover:underline cursor-pointer"
                              onClick={() => navigate(`/employees/${doc.employee_id}?tab=documents`)}
                              title={isRTL ? 'عرض مستندات الموظف' : 'View employee documents'}
                            >
                              {doc.employee_name || 'Unknown'}
                            </button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-slate-400" />
                            <span>{doc.document_type_name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {formatDate(doc.uploaded_at)}
                        </TableCell>
                        <TableCell>
                          {doc.expiry_date ? (
                            <div className="flex items-center gap-2">
                              {expired && <AlertTriangle className="h-4 w-4 text-red-500" />}
                              {expiringSoon && !expired && <AlertTriangle className="h-4 w-4 text-amber-500" />}
                              <span className={cn(
                                expired && "text-red-600 font-medium",
                                expiringSoon && !expired && "text-amber-600 font-medium"
                              )}>
                                {formatDate(doc.expiry_date)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_STYLES[doc.status]?.bg}>
                            <StatusIcon className="h-3 w-3 me-1" />
                            {getStatusLabel(doc.status)}
                          </Badge>
                        </TableCell>
                        {hasRole('hr_manager', 'hr_admin', 'super_admin') && (
                          <TableCell>
                            <OCRStatusBadge 
                              status={doc.ocr_status || 'not_run'} 
                              documentId={doc.id}
                              language={language}
                              size="sm"
                              pendingCount={doc.ocr_pending_count || 0}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setViewDialog({ open: true, document: doc })}
                              title={isRTL ? 'عرض' : 'View'}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownload(doc)}
                              title={isRTL ? 'تحميل' : 'Download'}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            {doc.status === 'pending' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  onClick={() => setReviewDialog({ open: true, document: doc, action: 'approved' })}
                                  data-testid={`approve-btn-${doc.id}`}
                                  title={isRTL ? 'موافقة' : 'Approve'}
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => setReviewDialog({ open: true, document: doc, action: 'rejected' })}
                                  data-testid={`reject-btn-${doc.id}`}
                                  title={isRTL ? 'رفض' : 'Reject'}
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <p className="text-sm text-slate-600">
                    {isRTL
                      ? `عرض ${(page - 1) * pageSize + 1} - ${Math.min(page * pageSize, total)} من ${total}`
                      : `Showing ${(page - 1) * pageSize + 1} - ${Math.min(page * pageSize, total)} of ${total}`}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                    </Button>
                    <span className="text-sm text-slate-600">
                      {page} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={reviewDialog.open} onOpenChange={(open) => !open && setReviewDialog({ open: false, document: null, action: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className={reviewDialog.action === 'approved' ? 'text-green-600' : 'text-red-600'}>
              {reviewDialog.action === 'approved'
                ? (isRTL ? 'اعتماد المستند' : 'Approve Document')
                : (isRTL ? 'رفض المستند' : 'Reject Document')}
            </DialogTitle>
            <DialogDescription>
              {reviewDialog.document && (
                <span>
                  {isRTL ? 'المستند:' : 'Document:'} {reviewDialog.document.document_type_name} - {reviewDialog.document.employee_name}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>
                {reviewDialog.action === 'approved'
                  ? (isRTL ? 'سبب الموافقة (مطلوب)' : 'Reason for approval (required)')
                  : (isRTL ? 'سبب الرفض (مطلوب)' : 'Reason for rejection (required)')}
              </Label>
              <Textarea
                value={reviewReason}
                onChange={(e) => setReviewReason(e.target.value)}
                placeholder={isRTL ? 'أدخل السبب (10 أحرف على الأقل)...' : 'Enter reason (at least 10 characters)...'}
                rows={4}
                data-testid="review-reason-input"
              />
              {reviewReason.length > 0 && reviewReason.length < 10 && (
                <p className="text-sm text-red-500">
                  {isRTL ? `${10 - reviewReason.length} أحرف متبقية` : `${10 - reviewReason.length} more characters needed`}
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialog({ open: false, document: null, action: null })}>
              {t('cancel')}
            </Button>
            <Button
              onClick={handleReviewSubmit}
              disabled={reviewReason.length < 10 || submitting}
              className={reviewDialog.action === 'approved' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              data-testid="confirm-review-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 me-2 animate-spin" />
                  {t('loading')}
                </>
              ) : (
                reviewDialog.action === 'approved'
                  ? (isRTL ? 'اعتماد' : 'Approve')
                  : (isRTL ? 'رفض' : 'Reject')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialog.open} onOpenChange={(open) => !open && setViewDialog({ open: false, document: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isRTL ? 'تفاصيل المستند' : 'Document Details'}</DialogTitle>
          </DialogHeader>
          
          {viewDialog.document && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">{isRTL ? 'الموظف' : 'Employee'}</p>
                  <p className="font-medium">{viewDialog.document.employee_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">{isRTL ? 'نوع المستند' : 'Document Type'}</p>
                  <p className="font-medium">{viewDialog.document.document_type_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">{isRTL ? 'اسم الملف' : 'File Name'}</p>
                  <p className="font-medium">{viewDialog.document.file_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">{isRTL ? 'الحالة' : 'Status'}</p>
                  <Badge className={STATUS_STYLES[viewDialog.document.status]?.bg}>
                    {getStatusLabel(viewDialog.document.status)}
                  </Badge>
                </div>
                <div>
                  <p className="text-slate-500">{isRTL ? 'تاريخ الرفع' : 'Upload Date'}</p>
                  <p className="font-medium">{formatDate(viewDialog.document.uploaded_at)}</p>
                </div>
                {viewDialog.document.expiry_date && (
                  <div>
                    <p className="text-slate-500">{isRTL ? 'تاريخ الانتهاء' : 'Expiry Date'}</p>
                    <p className={cn(
                      "font-medium",
                      isExpired(viewDialog.document) && "text-red-600",
                      isExpiringSoon(viewDialog.document) && !isExpired(viewDialog.document) && "text-amber-600"
                    )}>
                      {formatDate(viewDialog.document.expiry_date)}
                    </p>
                  </div>
                )}
              </div>

              {viewDialog.document.notes && (
                <div>
                  <p className="text-slate-500 text-sm mb-1">{isRTL ? 'ملاحظات' : 'Notes'}</p>
                  <p className="text-sm bg-slate-50 p-2 rounded">{viewDialog.document.notes}</p>
                </div>
              )}

              {viewDialog.document.review_reason && (
                <div>
                  <p className="text-slate-500 text-sm mb-1">{isRTL ? 'سبب المراجعة' : 'Review Reason'}</p>
                  <p className={cn(
                    "text-sm p-2 rounded",
                    viewDialog.document.status === 'approved' ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
                  )}>
                    {viewDialog.document.review_reason}
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialog({ open: false, document: null })}>
              {t('close')}
            </Button>
            {viewDialog.document && (
              <Button onClick={() => handleDownload(viewDialog.document)}>
                <Download className="h-4 w-4 me-2" />
                {t('download')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HRDocumentVerificationPage;
